import i18next from 'i18next';
import type { PlannerRegion, SeasonalityProfile } from '../../types';

export interface PlannerMonth {
  index: number;
  slug: string;
  shortLabel: string;
  label: string;
}

export interface PlannerRegionOption {
  value: PlannerRegion;
  shortLabel: string;
  label: string;
  description: string;
}

export interface SeasonalityWindow {
  months: number[];
  peakMonths: number[];
  shoulderMonths: number[];
  summary: string;
  confidence: 'year_round' | 'explicit' | 'seasonal' | 'structured' | 'unknown';
  yearRound: boolean;
  regions: PlannerRegion[];
  note?: string;
}

export type SeasonStatus = 'in_season' | 'shoulder' | 'out_of_season' | 'region_unverified' | 'unknown';

export interface SeasonStatusMeta {
  label: string;
  shortLabel: string;
  icon: string;
  badgeClassName: string;
  description: string;
}

const plannerText = (key: string, options?: Record<string, unknown>) => String(i18next.t(`codex:${key}`, options));

const monthOption = (index: number, slug: string): PlannerMonth => ({
  index,
  slug,
  get shortLabel() {
    return plannerText(`planner.monthShort.${index}`);
  },
  get label() {
    return plannerText(`planner.months.${index}`);
  }
});

const regionOption = (value: PlannerRegion): PlannerRegionOption => ({
  value,
  get shortLabel() {
    return plannerText(`planner.regions.${value}.shortLabel`);
  },
  get label() {
    return plannerText(`planner.regions.${value}.label`);
  },
  get description() {
    return plannerText(`planner.regions.${value}.description`);
  }
});

export const PLANNER_MONTHS: PlannerMonth[] = [
  monthOption(0, 'jan'),
  monthOption(1, 'feb'),
  monthOption(2, 'mar'),
  monthOption(3, 'apr'),
  monthOption(4, 'may'),
  monthOption(5, 'jun'),
  monthOption(6, 'jul'),
  monthOption(7, 'aug'),
  monthOption(8, 'sep'),
  monthOption(9, 'oct'),
  monthOption(10, 'nov'),
  monthOption(11, 'dec')
];

export const PLANNER_REGIONS: PlannerRegionOption[] = [
  regionOption('tyrrhenian'),
  regionOption('adriatic'),
  regionOption('ionian'),
  regionOption('sicily_channel'),
  regionOption('western_med'),
  regionOption('aegean_cyprus')
];

export const DEFAULT_PLANNER_REGION: PlannerRegion = 'tyrrhenian';

const ALL_REGION_VALUES = PLANNER_REGIONS.map((region) => region.value);

const MONTH_PATTERNS: Array<{ index: number; pattern: RegExp }> = [
  { index: 0, pattern: /\b(jan(?:uary)?|genn(?:aio)?|ene(?:ro)?|janv(?:ier)?)\b/i },
  { index: 1, pattern: /\b(feb(?:ruary)?|febb(?:raio)?|febr(?:ero)?|fevr(?:ier)?)\b/i },
  { index: 2, pattern: /\b(mar(?:ch|zo|s)?|mars)\b/i },
  { index: 3, pattern: /\b(apr(?:il|ile)?|abril|avr(?:il)?)\b/i },
  { index: 4, pattern: /\b(may|maggio|mayo|mai)\b/i },
  { index: 5, pattern: /\b(jun(?:e|io)?|giugno|juin)\b/i },
  { index: 6, pattern: /\b(jul(?:y|io)?|luglio|juillet)\b/i },
  { index: 7, pattern: /\b(aug(?:ust|usto)?|agosto|aout)\b/i },
  { index: 8, pattern: /\b(sep(?:t(?:ember|iembre|embre)?)?|settembre)\b/i },
  { index: 9, pattern: /\b(oct(?:ober|ubre)?|ottobre)\b/i },
  { index: 10, pattern: /\b(nov(?:ember|iembre|embre)?)\b/i },
  { index: 11, pattern: /\b(dec(?:ember|iembre|embre)?|dic(?:embre|iembre)?)\b/i }
];

const SEASON_GROUPS: Array<{ pattern: RegExp; months: number[] }> = [
  { pattern: /\b(spring|primavera|printemps)\b/i, months: [2, 3, 4] },
  { pattern: /\b(summer|estate|verano|ete)\b/i, months: [5, 6, 7] },
  { pattern: /\b(autumn|fall|autunno|otono|automne)\b/i, months: [8, 9, 10] },
  { pattern: /\b(winter|inverno|invierno|hiver)\b/i, months: [11, 0, 1] }
];

const YEAR_ROUND_PATTERN = /\b(year[ -]?round|all year|all-season|all season|throughout the year|todo el ano|tutto l'anno|toute l'annee)\b/i;

const SEASON_STATUS_META: Record<SeasonStatus, SeasonStatusMeta> = {
  in_season: {
    get label() {
      return plannerText('seasonality.status.inSeason.label');
    },
    get shortLabel() {
      return plannerText('seasonality.status.inSeason.shortLabel');
    },
    icon: '✅',
    badgeClassName: 'bg-mediterranean-green-50/90 text-mediterranean-green-800 border border-mediterranean-green-200/70',
    get description() {
      return plannerText('seasonality.status.inSeason.description');
    }
  },
  shoulder: {
    get label() {
      return plannerText('seasonality.status.shoulder.label');
    },
    get shortLabel() {
      return plannerText('seasonality.status.shoulder.shortLabel');
    },
    icon: '🟡',
    badgeClassName: 'bg-mediterranean-gold-50/90 text-mediterranean-gold-800 border border-mediterranean-gold-200/70',
    get description() {
      return plannerText('seasonality.status.shoulder.description');
    }
  },
  out_of_season: {
    get label() {
      return plannerText('seasonality.status.outOfSeason.label');
    },
    get shortLabel() {
      return plannerText('seasonality.status.outOfSeason.shortLabel');
    },
    icon: '⏸️',
    badgeClassName: 'bg-neutral-100/95 text-neutral-700 border border-neutral-200/80',
    get description() {
      return plannerText('seasonality.status.outOfSeason.description');
    }
  },
  region_unverified: {
    get label() {
      return plannerText('seasonality.status.regionUnverified.label');
    },
    get shortLabel() {
      return plannerText('seasonality.status.regionUnverified.shortLabel');
    },
    icon: '🧭',
    badgeClassName: 'bg-mediterranean-coral-50/90 text-mediterranean-coral-800 border border-mediterranean-coral-200/70',
    get description() {
      return plannerText('seasonality.status.regionUnverified.description');
    }
  },
  unknown: {
    get label() {
      return plannerText('seasonality.status.unknown.label');
    },
    get shortLabel() {
      return plannerText('seasonality.status.unknown.shortLabel');
    },
    icon: '📚',
    badgeClassName: 'bg-white/90 text-neutral-700 border border-neutral-200/80',
    get description() {
      return plannerText('seasonality.status.unknown.description');
    }
  }
};

const normalizeText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[()]/g, ' ')
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

const uniqueMonths = (months: number[]): number[] =>
  Array.from(new Set(months.filter((month) => Number.isInteger(month) && month >= 0 && month <= 11))).sort((left, right) => left - right);

const buildRange = (start: number, end: number): number[] => {
  const months: number[] = [start];
  let cursor = start;

  while (cursor !== end) {
    cursor = (cursor + 1) % 12;
    months.push(cursor);

    if (months.length > 12) {
      break;
    }
  }

  return months;
};

const extractExplicitMonths = (text: string): number[] => {
  const matches = MONTH_PATTERNS.flatMap(({ index, pattern }) => {
    const found = text.match(pattern);
    if (!found) {
      return [];
    }

    return [{ index, position: found.index ?? text.indexOf(found[0]) }];
  }).sort((left, right) => left.position - right.position);

  if (matches.length === 0) {
    return [];
  }

  if (matches.length === 1) {
    return [matches[0].index];
  }

  return buildRange(matches[0].index, matches[matches.length - 1].index);
};

const isSeasonalityProfile = (value: unknown): value is SeasonalityProfile => {
  return Boolean(value) && typeof value === 'object' && Array.isArray((value as SeasonalityProfile).peakMonths);
};

export const formatMonthRange = (months: number[]): string => {
  if (months.length === 0) {
    return plannerText('seasonality.timingNotSpecified');
  }

  if (months.length === 12) {
    return plannerText('seasonality.yearRound');
  }

  if (months.length === 1) {
    return PLANNER_MONTHS[months[0]].label;
  }

  return plannerText('seasonality.monthRange', {
    start: PLANNER_MONTHS[months[0]].label,
    end: PLANNER_MONTHS[months[months.length - 1]].label
  });
};

export const getCurrentPlannerMonth = (): number => new Date().getMonth();

export const getPlannerRegionMeta = (region: PlannerRegion): PlannerRegionOption =>
  PLANNER_REGIONS.find((option) => option.value === region) || PLANNER_REGIONS[0];

export const formatPlannerRegionLabel = (region: PlannerRegion): string => getPlannerRegionMeta(region).label;

export const formatPlannerRegionList = (regions: PlannerRegion[]): string => {
  if (!regions || regions.length === 0 || regions.length === ALL_REGION_VALUES.length) {
    return plannerText('seasonality.acrossMediterranean');
  }

  return regions.map((region) => getPlannerRegionMeta(region).shortLabel).join(' • ');
};

export const getActiveMonths = (window: SeasonalityWindow): number[] => {
  if (window.yearRound) {
    return PLANNER_MONTHS.map((month) => month.index);
  }

  if (window.peakMonths.length > 0 || window.shoulderMonths.length > 0) {
    return uniqueMonths([...window.peakMonths, ...window.shoulderMonths]);
  }

  return uniqueMonths(window.months);
};

export const isMonthActive = (window: SeasonalityWindow, monthIndex: number): boolean => {
  if (window.yearRound) {
    return true;
  }

  return getActiveMonths(window).includes(monthIndex);
};

export const isRegionSupported = (window: SeasonalityWindow, region: PlannerRegion): boolean => {
  if (!window.regions || window.regions.length === 0) {
    return true;
  }

  return window.regions.includes(region);
};

export const parseSeasonality = (value?: string | string[] | SeasonalityProfile | null): SeasonalityWindow => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return {
      months: [],
      peakMonths: [],
      shoulderMonths: [],
      summary: plannerText('seasonality.timingNotSpecified'),
      confidence: 'unknown',
      yearRound: false,
      regions: []
    };
  }

  if (isSeasonalityProfile(value)) {
    const peakMonths = uniqueMonths(value.peakMonths);
    const shoulderMonths = uniqueMonths((value.shoulderMonths || []).filter((month) => !peakMonths.includes(month)));
    const months = uniqueMonths([...peakMonths, ...shoulderMonths]);
    const regions = (value.regions || []).filter((region): region is PlannerRegion => ALL_REGION_VALUES.includes(region));
    const yearRound = peakMonths.length === 12;
    const summary = value.note || (yearRound
      ? plannerText('seasonality.yearRound')
      : shoulderMonths.length > 0
        ? plannerText('seasonality.peakShoulderSummary', { peak: formatMonthRange(peakMonths), shoulder: formatMonthRange(shoulderMonths) })
        : plannerText('seasonality.peakSummary', { peak: formatMonthRange(peakMonths) }));

    return {
      months,
      peakMonths,
      shoulderMonths,
      summary,
      confidence: 'structured',
      yearRound,
      regions,
      note: value.note
    };
  }

  const rawSummary = Array.isArray(value) ? value.join(', ') : value;
  const summary = rawSummary.trim();
  const normalized = normalizeText(summary);

  if (!normalized) {
    return {
      months: [],
      peakMonths: [],
      shoulderMonths: [],
      summary: plannerText('seasonality.timingNotSpecified'),
      confidence: 'unknown',
      yearRound: false,
      regions: []
    };
  }

  if (YEAR_ROUND_PATTERN.test(normalized)) {
    const months = PLANNER_MONTHS.map((month) => month.index);
    return {
      months,
      peakMonths: months,
      shoulderMonths: [],
      summary,
      confidence: 'year_round',
      yearRound: true,
      regions: []
    };
  }

  const explicitMonths = extractExplicitMonths(normalized);
  if (explicitMonths.length > 0) {
    const months = uniqueMonths(explicitMonths);
    return {
      months,
      peakMonths: months,
      shoulderMonths: [],
      summary,
      confidence: 'explicit',
      yearRound: false,
      regions: []
    };
  }

  const seasonalMonths = SEASON_GROUPS.flatMap(({ pattern, months }) => (pattern.test(normalized) ? months : []));
  if (seasonalMonths.length > 0) {
    const months = uniqueMonths(seasonalMonths);
    return {
      months,
      peakMonths: months,
      shoulderMonths: [],
      summary,
      confidence: 'seasonal',
      yearRound: false,
      regions: []
    };
  }

  return {
    months: [],
    peakMonths: [],
    shoulderMonths: [],
    summary,
    confidence: 'unknown',
    yearRound: false,
    regions: []
  };
};

export const getSeasonStatus = (window: SeasonalityWindow, monthIndex: number, region: PlannerRegion): SeasonStatus => {
  if (!isRegionSupported(window, region)) {
    return 'region_unverified';
  }

  if (window.yearRound || window.peakMonths.includes(monthIndex) || (window.peakMonths.length === 0 && window.months.includes(monthIndex))) {
    return 'in_season';
  }

  if (window.shoulderMonths.includes(monthIndex)) {
    return 'shoulder';
  }

  if (window.months.length > 0) {
    return 'out_of_season';
  }

  return 'unknown';
};

export const scoreSeasonStatus = (window: SeasonalityWindow, monthIndex: number, region: PlannerRegion, scores?: Partial<Record<SeasonStatus, number>>): number => {
  const defaults: Record<SeasonStatus, number> = {
    in_season: 16,
    shoulder: 6,
    out_of_season: -14,
    region_unverified: -8,
    unknown: 0
  };

  const status = getSeasonStatus(window, monthIndex, region);
  return scores?.[status] ?? defaults[status];
};

export const getSeasonStatusMeta = (status: SeasonStatus): SeasonStatusMeta => SEASON_STATUS_META[status];
